import React, { useState, useEffect, useContext } from 'react';
import UserContext from '../../../components/User_Context.js';
import * as menu from "../../../assets/menus/menu.js";
import * as breadcrumb from "../../../components/ui/breadcrumb.js";
import * as api from "../../../api/apiConnector.js";
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Choices from '../../../components/Choices.js';
import Form_Compiler from './compilerNew.js';
import { formBuilderLabels } from '../../_label/form_builder/form_builder_labels.js';

const Validation_Dashboard = () => {
    const { currentUser } = useContext(UserContext);
    const { t } = useTranslation();
    const MySwal = withReactContent(Swal);

    // Filter states
    const [selectedUser, setSelectedUser] = useState("");
    const [selectedUserObject, setSelectedUserObject] = useState(null);
    const [selectedForm, setSelectedForm] = useState("");
    const [users, setUsers] = useState([]);
    const [formList, setFormList] = useState([]);

    // Data states
    const [forms, setForms] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    // Form viewer state
    const [viewingForm, setViewingForm] = useState(null);
    const [viewingFormData, setViewingFormData] = useState(null);

    // Initial setup on component mount
    useEffect(() => {
        menu.write({ user: currentUser() });

        breadcrumb.set([
            { link: "/", name: t('home', { ns: 'labels' }) },
            { link: "/form_builder/validation", name: t('validationDashboard', { ns: 'labels' }) }
        ]);

        loadInitialData();
    }, []);

    // Initialize translations from centralized file
    useEffect(() => {
        const i18n = menu.getI18n();
        i18n.addResourceBundle('it', 'labels', formBuilderLabels.it, true, true);
        i18n.addResourceBundle('en', 'labels', formBuilderLabels.en, true, true);
    }, []);

    // Load initial data (users and forms for dropdowns)
    const loadInitialData = async () => {
        try {
            // Load users
            const usersResult = await api.sendMessage("admin/security/getAllUsers", {
                user: currentUser(),
                body: {}
            });

            if (usersResult?.success && usersResult?.users) {
                const formattedUsers = usersResult.users.map(user => {
                    const name = user.Name || '';
                    const surname = user.LastName || '';
                    const email = user.EMail || '';
                    
                    let displayName = name;
                    if (surname) {
                        displayName += ` ${surname}`;
                    }
                    if (email) {
                        displayName += ` (${email})`;
                    }
                    
                    return {
                        Code: user.Id || user.id || user.ID,
                        Name: displayName || email || user.Username || 'Utente sconosciuto',
                        OriginalUser: user
                    };
                });
                setUsers(formattedUsers);
            }

            // Load forms for dropdown
            const formsResult = await api.sendMessage("darm/formBuilder/finder", {
                user: currentUser(),
                body: {}
            });

            if (formsResult?.filtered) {
                const formattedForms = formsResult.filtered.map(form => ({
                    Code: form.Id,
                    Name: form.FormName
                }));
                setFormList(formattedForms);
            }
        } catch (error) {
            console.error("Error loading initial data:", error);
            MySwal.fire({
                title: t('error', { ns: 'labels' }),
                text: error.message || t('error', { ns: 'labels' }),
                icon: 'error'
            });
        }
    };

    // Search for forms based on filters
    const handleSearch = async () => {
        try {
            setLoading(true);
            setHasSearched(true);

            // Prepare the request body based on selected filters
            const requestBody = {
                admin: true // Get all forms for validation dashboard
            };

            // If specific user is selected, filter by that user
            if (selectedUserObject) {
                requestBody.userId = selectedUserObject.Id;
                requestBody.admin = false; // Override admin mode when filtering by user
            }

            const result = await api.sendMessage("darm/formCompletion/getFormsPerUser", {
                user: currentUser(),
                body: requestBody
            });

            if (result) {
                let filteredResult = result;

                // If form is selected, filter the results by form
                if (selectedForm) {
                    const filterByForm = (formsList) => 
                        formsList.filter(form => form.FormId === selectedForm);

                    filteredResult = {
                        assigned: filterByForm(result.assigned || []),
                        saved: filterByForm(result.saved || []),
                        compiled: filterByForm(result.compiled || []),
                        approved: filterByForm(result.approved || []),
                        metadata: result.metadata
                    };
                }

                setForms(filteredResult);
            }
        } catch (error) {
            console.error("Error searching forms:", error);
            MySwal.fire({
                title: t('error', { ns: 'labels' }),
                text: error.message || t('error', { ns: 'labels' }),
                icon: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    // Clear all filters and results
    const handleClear = () => {
        setSelectedUser("");
        setSelectedUserObject(null);
        setSelectedForm("");
        setForms(null);
        setHasSearched(false);
    };

    // Handle form validation
    const handleValidateForm = async (completionId, formName) => {
        MySwal.fire({
            title: t('attention', { ns: 'labels' }),
            text: t('confirmValidation', { ns: 'labels' }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('confirm', { ns: 'labels' }),
            cancelButtonText: t('cancel', { ns: 'labels' })
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await api.sendMessage("darm/formCompletion/validate", {
                        user: currentUser(),
                        body: {
                            completionId: completionId
                        }
                    });

                    if (response.success) {
                        MySwal.fire({
                            title: t('success', { ns: 'labels' }),
                            text: t('validationSuccess', { ns: 'labels' }),
                            icon: 'success',
                            timer: 2000
                        });
                        // Refresh the search results
                        handleSearch();
                    } else {
                        MySwal.fire({
                            title: t('error', { ns: 'labels' }),
                            text: response.message || t('validationError', { ns: 'labels' }),
                            icon: 'error'
                        });
                    }
                } catch (error) {
                    MySwal.fire({
                        title: t('error', { ns: 'labels' }),
                        text: t('validationError', { ns: 'labels' }),
                        icon: 'error'
                    });
                }
            }
        });
    };

    // Handle viewing form in read-only mode
    const handleViewForm = async (item) => {
        try {
            // Show loading indicator
            MySwal.fire({
                title: t('loading', { ns: 'labels' }),
                text: t('loadingFormData', { ns: 'labels', defaultValue: 'Loading form data...' }),
                allowOutsideClick: false,
                showConfirmButton: false,
                willOpen: () => {
                    Swal.showLoading();
                }
            });

            // Load both the form completion data AND the form structure
            const [completionResult, formResult] = await Promise.all([
                // Load the form completion data
                api.sendMessage("darm/formCompletion/get", {
                    user: currentUser(),
                    body: {
                        formId: item.FormId,
                        completionId: item.Id
                    }
                }),
                // Load the form structure
                api.sendMessage("darm/formBuilder/get", {
                    user: currentUser(),
                    body: {
                        id: item.FormId
                    }
                })
            ]);

            MySwal.close();

            if (completionResult?.success && completionResult?.formCompletion && formResult?.success && formResult?.form) {
                // Combine the form structure with the completion data
                const combinedData = {
                    ...completionResult.formCompletion,
                    form: formResult.form
                };
                
                console.log("🔍 [DEBUG] Combined form data:", combinedData);
                
                // Set the viewing form data
                setViewingForm(item);
                setViewingFormData(combinedData);
                // Update breadcrumb when viewing a form
                updateBreadcrumbForFormView(item.formMeta?.Name || 'Form');
            } else {
                MySwal.fire({
                    title: t('error', { ns: 'labels' }),
                    text: completionResult?.message || formResult?.message || t('failedToLoadForm', { ns: 'labels', defaultValue: 'Failed to load form data' }),
                    icon: 'error'
                });
            }
        } catch (error) {
            MySwal.close();
            console.error("Error loading form for viewing:", error);
            MySwal.fire({
                title: t('error', { ns: 'labels' }),
                text: error.message || t('failedToLoadForm', { ns: 'labels', defaultValue: 'Failed to load form data' }),
                icon: 'error'
            });
        }
    };

    // Handle closing the form viewer
    const handleCloseFormViewer = () => {
        setViewingForm(null);
        setViewingFormData(null);
        // Reset breadcrumb to dashboard
        breadcrumb.set([
            { link: "/", name: t('home', { ns: 'labels' }) },
            { link: "/form_builder/validation", name: t('validationDashboard', { ns: 'labels' }) }
        ]);
    };

    // Update breadcrumb when viewing a form
    const updateBreadcrumbForFormView = (formName) => {
        breadcrumb.set([
            { link: "/", name: t('home', { ns: 'labels' }) },
            { link: "/form_builder/validation", name: t('validationDashboard', { ns: 'labels' }) },
            { link: "#", name: `${t('viewingForm', { ns: 'labels' })}: ${formName}` }
        ]);
    };

    // Format date for display
    const formatDate = (isoString) => {
        if (!isoString) return "";
        const date = new Date(isoString);
        return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
    };

    // TableCard component to render each category of forms
    const TableCard = ({ label, labelKey, list = [] }) => {
        const [collapsed, setCollapsed] = useState(list.length === 0);

        return (
            <div className="card mb-4" style={{ border: '2px solid #87CEEB', borderRadius: '8px' }}>
                <div
                    className="card-header d-flex justify-content-between align-items-center"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setCollapsed(!collapsed)}
                >
                    <h5 className="mb-0 fw-semibold">{label} ({list.length})</h5>
                    <span>{collapsed ? '+' : '−'}</span>
                </div>

                {!collapsed && (
                    <div className="card-body">
                        <div style={{ minHeight: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {list.length > 0 ? (
                                <div className="w-100">
                                    {/* Desktop/Tablet */}
                                    <div className="d-none d-md-block" style={{ overflowX: 'auto', width: '100%' }}>
                                        <table className="table table-hover" style={{ minWidth: '1200px', borderCollapse: 'separate', borderSpacing: '0 12px' }}>
                                            <thead>
                                                <tr className="text-muted" style={{ fontSize: '0.875rem' }}>
                                                    <th style={{ padding: '8px 16px', minWidth: '200px' }}>{t('Name', { ns: 'labels' })}</th>
                                                    <th style={{ padding: '8px 16px', minWidth: '250px' }}>{t('Description', { ns: 'labels' })}</th>
                                                    <th style={{ padding: '8px 16px', minWidth: '150px' }}>{t('Group', { ns: 'labels' })}</th>
                                                    <th style={{ padding: '8px 16px', minWidth: '100px' }}>{t('Code', { ns: 'labels' })}</th>
                                                    <th style={{ padding: '8px 16px', minWidth: '120px' }}>{t('CreatedDate', { ns: 'labels' })}</th>
                                                    <th style={{ padding: '8px 16px', minWidth: '100px' }}>{t('actions', { ns: 'labels' })}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {list.map(item => (
                                                    <tr key={item.Id} style={{ backgroundColor: '#f8f9fa', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                                        <td style={{ padding: '12px 16px', borderTopLeftRadius: '6px', borderBottomLeftRadius: '6px' }}>{item.formMeta?.Name}</td>
                                                        <td style={{ padding: '12px 16px' }}>{item.formMeta?.Description}</td>
                                                        <td style={{ padding: '12px 16px' }}>{item.formMeta?.Group}</td>
                                                        <td style={{ padding: '12px 16px' }}>{item.formMeta?.Code}</td>
                                                        <td style={{ padding: '12px 16px' }}>{formatDate(item.ItemCreated)}</td>
                                                        <td style={{ padding: '12px 16px', borderTopRightRadius: '6px', borderBottomRightRadius: '6px' }}>
                                                            {/* Pulsante Visualizza per tutte le compilazioni e approvazioni */}
                                                            {((item.Status === 'Compilata' || item.Status === 'Compiled') || (item.Status === 'Approvata' || item.Status === 'Approved')) && (
                                                                <button
                                                                    className="btn btn-sm btn-info me-2"
                                                                    onClick={() => handleViewForm(item)}
                                                                    title={t('viewForm', { ns: 'labels' })}
                                                                >
                                                                    <i className="fa-solid fa-eye me-1"></i>
                                                                    {t('view', { ns: 'labels' })}
                                                                </button>
                                                            )}
                                                            
                                                            {/* Pulsante Valida solo per quelle che richiedono validazione */}
                                                            {(item.Status === 'Compilata' || item.Status === 'Compiled') && item.ToValidate && !item.Validated && (
                                                                <button
                                                                    className="btn btn-sm btn-success"
                                                                    onClick={() => handleValidateForm(item.Id, item.formMeta?.Name)}
                                                                    title={t('validate', { ns: 'labels' })}
                                                                >
                                                                    <i className="fa-solid fa-check me-1"></i>
                                                                    {t('validate', { ns: 'labels' })}
                                                                </button>
                                                            )}
                                                            
                                                            {/* Badge Validato */}
                                                            {item.Validated && (
                                                                <span className="badge bg-success">
                                                                    <i className="fa-solid fa-check me-1"></i>
                                                                    {t('validated', { ns: 'labels' })}
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Mobile */}
                                    <div className="d-block d-md-none responsive-table">
                                        {list.map(item => (
                                            <div key={item.Id} className="table-row-responsive">
                                                <div className="table-cell-responsive"><strong>{t('Name', { ns: 'labels' })}:</strong> {item.formMeta?.Name}</div>
                                                <div className="table-cell-responsive"><strong>{t('Description', { ns: 'labels' })}:</strong> {item.formMeta?.Description}</div>
                                                <div className="table-cell-responsive"><strong>{t('Group', { ns: 'labels' })}:</strong> {item.formMeta?.Group}</div>
                                                <div className="table-cell-responsive"><strong>{t('Code', { ns: 'labels' })}:</strong> {item.formMeta?.Code}</div>
                                                <div className="table-cell-responsive"><strong>{t('CreatedDate', { ns: 'labels' })}:</strong> {formatDate(item.ItemCreated)}</div>
                                                <div className="table-cell-responsive">
                                                    {/* Pulsante Visualizza per tutte le compilazioni e approvazioni */}
                                                    {((item.Status === 'Compilata' || item.Status === 'Compiled') || (item.Status === 'Approvata' || item.Status === 'Approved')) && (
                                                        <button
                                                            className="btn btn-sm btn-info me-2"
                                                            onClick={() => handleViewForm(item)}
                                                            title={t('viewForm', { ns: 'labels' })}
                                                        >
                                                            <i className="fa-solid fa-eye me-1"></i>
                                                            {t('view', { ns: 'labels' })}
                                                        </button>
                                                    )}
                                                    
                                                    {/* Pulsante Valida solo per quelle che richiedono validazione */}
                                                    {(item.Status === 'Compilata' || item.Status === 'Compiled') && item.ToValidate && !item.Validated && (
                                                        <button
                                                            className="btn btn-sm btn-success"
                                                            onClick={() => handleValidateForm(item.Id, item.formMeta?.Name)}
                                                        >
                                                            <i className="fa-solid fa-check me-1"></i>
                                                            {t('validate', { ns: 'labels' })}
                                                        </button>
                                                    )}
                                                    
                                                    {/* Badge Validato */}
                                                    {item.Validated && (
                                                        <span className="badge bg-success">
                                                            <i className="fa-solid fa-check me-1"></i>
                                                            {t('validated', { ns: 'labels' })}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-muted text-center" style={{ fontSize: '0.95rem' }}>
                                    {t(`empty${labelKey}`, { ns: 'labels' })}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Calculate total forms
    const totalForms = forms ? 
        (forms.assigned?.length || 0) +
        (forms.saved?.length || 0) +
        (forms.compiled?.length || 0) +
        (forms.approved?.length || 0) : 0;

    return (
        <>
            {/* Se stiamo visualizzando un form, mostra il Form_Compiler in modalità read-only */}
            {viewingForm && viewingFormData ? (
                <div className="container-fluid">
                    <Form_Compiler
                        form={viewingFormData.form}
                        formCompletion={viewingFormData}
                        language="it"
                        readOnly={true}
                        onClose={handleCloseFormViewer}
                        colors={{
                            primary: '#f05f23',
                            primaryHover: '#d44d12',
                            primaryLight: '#fef0ea',
                            secondary: '#6c757d',
                            secondaryLight: '#e9ecef',
                            dark: '#343a40',
                            light: '#f8f9fa',
                            white: '#ffffff',
                            success: '#28a745',
                            danger: '#dc3545',
                            warning: '#ffc107',
                            info: '#17a2b8'
                        }}
                    />
                </div>
            ) : (
                <>
                    {/* Dashboard normale */}
                    {/* Breadcrumb header */}
                    <div className="page-titles">
                        <ol className="breadcrumb"></ol>
                    </div>

                    <div className="container-fluid">
                        {/* Filter Card */}
                        <div className="row">
                            <div className="col-xl-12 col-lg-12">
                                <div className="card">
                                    <div className="card-header">
                                        <h4 className="card-title">{t('filter', { ns: 'labels' })}</h4>
                                    </div>
                                    <div className="card-body">
                                        <div className="basic-form row">
                                            {/* User selection */}
                                            <div className="mb-3 col-md-6">
                                                <label className="form-label">{t('selectUser', { ns: 'labels' })}</label>
                                                <Choices
                                                    voc={users}
                                                    value="Code"
                                                    text="Name"
                                                    className="inputStyle blackText bg-white"
                                                    selected={selectedUser ? [{ Code: selectedUser }] : []}
                                                    onChange={(e) => {
                                                        const selected = e.selected?.[0]?.Code || "";
                                                        const selectedUserData = users.find(user => user.Code === selected);
                                                        setSelectedUser(selected);
                                                        setSelectedUserObject(selectedUserData?.OriginalUser || null);
                                                    }}
                                                    placeholder={t('selectUser', { ns: 'labels' })}
                                                    dropDownParent="root"
                                                />
                                            </div>

                                            {/* Form selection */}
                                            <div className="mb-3 col-md-6">
                                                <label className="form-label">{t('selectForm', { ns: 'labels' })}</label>
                                                <Choices
                                                    voc={formList}
                                                    value="Code"
                                                    text="Name"
                                                    className="inputStyle blackText bg-white"
                                                    selected={selectedForm ? [{ Code: selectedForm }] : []}
                                                    onChange={(e) => {
                                                        const selected = e.selected?.[0]?.Code || "";
                                                        setSelectedForm(selected);
                                                    }}
                                                    placeholder={t('selectForm', { ns: 'labels' })}
                                                    dropDownParent="root"
                                                />
                                            </div>

                                            {/* Search and clear buttons */}
                                            <div className="mb-3 col-md-12">
                                                <button
                                                    type="button"
                                                    className="btn btn-primary"
                                                    onClick={handleSearch}
                                                    disabled={loading}
                                                >
                                                    {loading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            {t('loading', { ns: 'labels' })}
                                                        </>
                                                    ) : (
                                                        t('search', { ns: 'labels' })
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary ms-2"
                                                    onClick={handleClear}
                                                    disabled={loading}
                                                >
                                                    {t('clear', { ns: 'labels' })}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Results section */}
                        {hasSearched && (
                            <>
                                {/* Dashboard header card */}
                                <div className="card mb-3" style={{ border: '1px solid #dee2e6', borderRadius: '8px' }}>
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                                            <div>
                                                <h4 className="card-title mb-1">{t('validationDashboard', { ns: 'labels' })}</h4>
                                                <p className="mb-0">
                                                    {t('validation_dashboard_welcome', { defaultValue: 'Gestisci la validazione dei form compilati.' })}
                                                </p>
                                            </div>
                                            {forms && (
                                                <h5 className="mb-0">
                                                    Totale: {totalForms}
                                                </h5>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Cards for each form status */}
                                {forms ? (
                                    <>
                                        <TableCard labelKey="Assigned" label={t("Assigned", { ns: "labels" })} list={forms.assigned} />
                                        <TableCard labelKey="Saved" label={t("Saved", { ns: "labels" })} list={forms.saved} />
                                        <TableCard labelKey="Compiled" label={t("Compiled", { ns: "labels" })} list={forms.compiled} />
                                        <TableCard labelKey="Approved" label={t("Approved", { ns: "labels" })} list={forms.approved} />
                                    </>
                                ) : (
                                    <div className="text-center p-4">
                                        <p>{t('noResults', { ns: 'labels' })}</p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </>
            )}
        </>
    );
};

export default Validation_Dashboard; 